// src/components/Navigation.js
import React, { useContext, useState } from 'react';
import { Container, Navbar, Nav, Dropdown, Image } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { UserContext } from '../Contexts/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDna, FaUser, FaSignOutAlt, FaUserCircle, FaHome, FaCogs, FaInfoCircle, FaDollarSign, FaServer } from 'react-icons/fa';

function Navigation() {
    const { user } = useContext(UserContext);
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);

    const styles = {
        navbar: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            padding: '1rem 0',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        },
        container: {
            maxWidth: '1200px',
        },
        brand: {
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            textDecoration: 'none',
            transition: 'opacity 0.2s ease',
            '&:hover': {
                opacity: '0.9',
            },
        },
        navLink: {
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.95rem',
            fontWeight: '500',
            padding: '0.5rem 1rem',
            margin: '0 0.25rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease',
            '&:hover': {
                color: '#ffffff',
                background: 'rgba(255,255,255,0.1)',
            },
        },
        activeLink: {
            color: '#ffffff',
            background: 'rgba(255,255,255,0.15)',
        },
        dropdownToggle: {
            background: 'transparent',
            border: 'none',
            padding: '0.25rem',
            borderRadius: '50%',
            overflow: 'hidden',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'scale(1.05)',
            },
        },
        profileImage: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.2)',
        },
        dropdownMenu: {
            marginTop: '0.5rem',
            padding: '0.5rem',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            background: '#ffffff',
        },
        dropdownItem: {
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            color: '#4a5568',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f7fafc',
                color: '#2C5282',
            },
        },
        icon: {
            fontSize: '1.2rem',
        },
        toggleButton: {
            border: 'none',
            background: 'transparent',
            padding: '0.5rem',
            color: '#ffffff',
        },
    };

    const isActive = (path) => location.pathname === path;

    return (
        <Navbar
            expand="lg"
            style={styles.navbar}
            expanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
        >
            <Container style={styles.container}>
                <Link to="/" style={styles.brand}>
                    <FaDna style={styles.icon} />
                    DNACloudHub
                </Link>

                <Navbar.Toggle
                    aria-controls="navbar-nav"
                    style={styles.toggleButton}
                />

                <Navbar.Collapse id="navbar-nav">
                    <Nav className="ms-auto">
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Nav.Link
                                as={Link}
                                to="/"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/') ? styles.activeLink : {})
                                }}
                            >
                                <FaHome style={styles.icon} /> Home
                            </Nav.Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                        >
                            <Nav.Link
                                as={Link}
                                to="/about"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/about') ? styles.activeLink : {})
                                }}
                            >
                                <FaInfoCircle style={styles.icon} /> About
                            </Nav.Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                        >
                            <Nav.Link
                                as={Link}
                                to="/services"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/services') ? styles.activeLink : {})
                                }}
                            >
                                <FaCogs style={styles.icon} /> Services
                            </Nav.Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.3 }}
                        >
                            <Nav.Link
                                as={Link}
                                to="/pricing"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/pricing') ? styles.activeLink : {})
                                }}
                            >
                                <FaDollarSign style={styles.icon} /> Pricing
                            </Nav.Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                        >
                            <Nav.Link
                                as={Link}
                                to="/run_qpadm"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/run_qpadm') ? styles.activeLink : {})
                                }}
                            >
                                <FaServer style={styles.icon} /> Run qpAdm
                            </Nav.Link>
                        </motion.div>

                        <AnimatePresence>
                            {!user && (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Nav.Link
                                        as={Link}
                                        to="/signup"
                                        style={{
                                            ...styles.navLink,
                                            ...(isActive('/signup') ? styles.activeLink : {})
                                        }}
                                    >
                                        <FaUserCircle style={styles.icon} /> Sign Up
                                    </Nav.Link>
                                </motion.div>
                            )}
                        </AnimatePresence>


                        <AnimatePresence>
                            {!user && (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Nav.Link
                                        as={Link}
                                        to="/login"
                                        style={{
                                            ...styles.navLink,
                                            ...(isActive('/login') ? styles.activeLink : {})
                                        }}
                                    >
                                        <FaUser style={styles.icon} /> Login
                                    </Nav.Link>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <AnimatePresence>
                            {user && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Dropdown align="end">
                                        <Dropdown.Toggle
                                            variant="link"
                                            id="user-profile-dropdown"
                                            style={styles.dropdownToggle}
                                        >
                                            <Image
                                                src="/user_profile.jpg"
                                                style={styles.profileImage}
                                                alt="User Profile"
                                            />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu style={styles.dropdownMenu}>
                                            <Dropdown.Item
                                                as={Link}
                                                to="/user_profile"
                                                style={styles.dropdownItem}
                                            >
                                                <FaUser style={styles.icon} /> Profile
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                as={Link}
                                                to="/logout"
                                                style={styles.dropdownItem}
                                            >
                                                <FaSignOutAlt style={styles.icon} /> Logout
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
}

export default Navigation;
